import { TransactionCard } from "components/pages/Questionnaire/components/IncomeExpenses/ProfileIncomeExpenses";

export const amountQuestionIdsWithSuggestions = ["INTEREST_AMOUNT", "STATE_BENEFITS_CHILD_BENEFIT_BENEFIT_PAYMENT", "PENSION_INCOME_STATE_AMOUNT", "PENSION_INCOME_PRIVATE_AMOUNT", "RENTAL_GROSS_INCOME"];
export const sectionToSuggestionTypes: Record<string, string | string[]> = {
    "State Benefits": ["childBenefitRecipient"],
    "Child Benefit": ["childBenefitRecipient"],
    "Primary Income": ["rentalEarner"],
    "Additional Income": ["interestEarner", "childBenefitRecipient", "rentalEarner", "pensionIncomeRecipient"],
    "Interest": ["interestEarner"],
    "Pension Income": ["pensionIncomeRecipient"],
    "Rental": ["rentalEarner"],
};

export const suggestionTypeToCleanSuggestion: Record<string, string | string[]> = {
    "childBenefitRecipient": "Child Benefit",
    "interestEarner": "Interest",
    "rentalEarner": "Rental",
    "pensionIncomeRecipient": "Pension",
};

export const suggestionTypesForPrimaryIncome = ["rentalEarner"];
export const suggestionTypesForAdditionalIncome = ["childBenefitRecipient", "interestEarner", "rentalEarner", "pensionIncomeRecipient"];

type Transaction = { amount: number };

export const createAmountSuggestionMessage = (
    suggestion: any
): string | null => {
    if (!suggestion || !suggestion.supportingData?.transactions?.length) return null;

    const transactions: Transaction[] = suggestion.supportingData.transactions;
    const numPayments = transactions.length;
    const total = transactions.reduce((acc, tx) => acc + tx.amount, 0);
    const formattedAmount = total.toLocaleString("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
    });

    const suggestionLabel = suggestionTypeToCleanSuggestion[suggestion.type] || "suggested";

    return `Your banking data seems to contain <strong>${numPayments} ${suggestionLabel}</strong> transaction${numPayments > 1 ? "s" : ""}.<br/>The total of these transactions is <strong>${formattedAmount}</strong>.`;
};

type Suggestion = {
    type: string;
    isSuggested: boolean;
    supportingData?: {
        transactions?: Transaction[];
    };
};

export const formatIncomeSuggestions = (
    suggestions: Suggestion[]
): { primaryIncomeSuggestion?: string; additionalIncomeSuggestion?: string } | null => {
    if (!Array.isArray(suggestions) || suggestions.length === 0) return null;

    const validSuggestions = suggestions.filter(
        s => s.isSuggested && s.supportingData?.transactions?.length
    );

    if (validSuggestions.length === 0) return null;

    const extractLabels = (types: string[]) =>
        validSuggestions.flatMap(s => {
            const label = suggestionTypeToCleanSuggestion[s.type];
            if (types.includes(s.type)) {
                return Array.isArray(label) ? label : [label];
            }
            return [];
        }).filter(Boolean) as string[];

    const primary = extractLabels(suggestionTypesForPrimaryIncome);
    const additional = extractLabels(suggestionTypesForAdditionalIncome);

    const uniquePrimary = Array.from(new Set(primary));
    const uniqueAdditional = Array.from(new Set(additional));

    const formatList = (items: string[]) => {
        if (items.length === 1) return items[0];
        return items.slice(0, -1).join(", ") + " and " + items.slice(-1);
    };

    const result: {
        primaryIncomeSuggestion?: string;
        additionalIncomeSuggestion?: string;
    } = {};

    if (uniquePrimary.length > 0) {
        result.primaryIncomeSuggestion = `Your banking data suggests you've received <strong>${formatList(uniquePrimary)}</strong> income.`;
    }

    if (uniqueAdditional.length > 0) {
        result.additionalIncomeSuggestion = `Your banking data suggests you've received <strong>${formatList(uniqueAdditional)}</strong> income.`;
    }

    return Object.keys(result).length > 0 ? result : null;
};

const getDerivedCategory = (tx: any, taxProfileType: string): string => {
    const rawCategory = tx?.category?.byTaxProfileType?.[taxProfileType]?.derivedCategory || "";
    return rawCategory === "Rent-a-Room" ? "Rental" : rawCategory;
};

export const mapToTransactionCards = (
    transactions: any[],
    taxProfileType: string
): TransactionCard[] => {
    return transactions.map(tx => {
        const derivedCategory = getDerivedCategory(tx, taxProfileType);
        return {
            id: tx?._id,
            totalAmount: Math.abs(Number(tx?.clientUpdatedAmount)) || Math.abs(Number(tx?.amount)) || 0,
            merchant: {
                name: tx?.merchant?.derivedMerchant || "",
                logo: tx?.merchant?.merchantLogo || "",
            },
            bookingDateTime: tx?.bookingDateTime,
            description: tx?.additionalDetails || tx?.reference,
            currencyCode: tx?.currencyCode || "GBP",
            tags: [
                tx?.category?.byTaxProfileType?.[taxProfileType]?.clientUpdatedCategory ||
                derivedCategory ||
                "No Category",
            ],
            swiped: tx?.swiped,
            category: tx?.category,

            transactionsCount: 1,
            earliestDate: tx?.bookingDateTime,
            latestDate: tx?.bookingDateTime,
            derivedCategory,
            externalTransactionId: tx.externalTransactionId,
        };
    });
};

export const transactionTaxProfileTypeFromQuestionnaireSection: Record<string, string> = {
    "STATE_BENEFITS_CHILD_BENEFIT_BENEFIT_PAYMENT": "Child Benefit",
    "INTEREST_AMOUNT": "Interest",
    "RENTAL_GROSS_INCOME": "Rent-a-Room",
    "PENSION_INCOME_PRIVATE_AMOUNT": "Pension",
    "PENSION_INCOME_STATE_AMOUNT": "Pension",
};
